import type { TReturnReason } from "@repo/shopify/api/requestReturn";
import { ReturnReason } from "@repo/shopify/api/requestReturn";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import classNames from "classnames";
import React, { type ChangeEvent, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import Image from "@atoms/Image";
import Input from "@atoms/Input";
import Price from "@atoms/Price";
import Select from "@atoms/Select";
import TextArea from "@atoms/TextArea";

import Stepper from "@molecules/Stepper";

import type { IReturnRequestedItem } from "@pages/Return/ReturnRequest";

import { RETURN_REASON_OPTIONS, translationReturnReason } from "@const/constant";
import { NOTIFICATIONS } from "@const/notifications";

import { useLayoutStore } from "@store/layout";

interface IProps {
  id: string;
  size: string;
  imageUrl?: string;
  title: string;
  variantTitle: string;
  returnQuantity: number; // 반품하려는 수량
  originalQuantity: number; // 구매당시 수량
  price: string;
  compareAtPrice: string;
  disabled: boolean;
  currentStep: "request" | "requested";
  returnRequestItem?: IReturnRequestedItem | undefined;
  onChangeReturnRequestItems?: (id: string, updatedFields: Partial<IReturnRequestedItem>) => void;
  returnReason?: string;
  customerNote?: string;
  isGift?: boolean;
}

export default function ReturnLineItem({
  id,
  size,
  imageUrl,
  title,
  variantTitle,
  returnQuantity,
  originalQuantity,
  price,
  compareAtPrice,
  disabled,
  currentStep,
  returnReason,
  customerNote,
  returnRequestItem,
  onChangeReturnRequestItems,
  isGift,
}: IProps) {
  const { isPC } = useCheckDevice();
  const { t } = useTranslation();
  const { openToast } = useLayoutStore();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const noteRef = useRef<string>("");

  const handleStepper = (num: number) => {
    if (num < 1) {
      openToast(NOTIFICATIONS.WARNING_NO_PRODUCT);
      return;
    }

    if (num > returnQuantity) {
      openToast(NOTIFICATIONS.WARNING_OVER_PRODUCT);
      return;
    }

    onChangeReturnRequestItems?.(id, { quantity: num });
  };

  const handleChangeReason = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = RETURN_REASON_OPTIONS.find((item) => item.value === (e.target.value as TReturnReason));
    const value = selectedOption?.value;

    const updatedImage = value === ReturnReason.DEFECTIVE ? (returnRequestItem?.image ?? null) : null;

    onChangeReturnRequestItems?.(id, { image: updatedImage, returnReason: value });
  };

  const handleInputFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length > 0) {
      onChangeReturnRequestItems?.(id, { image: selectedFiles });
    }
  };

  const images = useMemo(() => {
    if (!returnRequestItem?.image) return "";
    return returnRequestItem.image || [];
  }, [returnRequestItem]);

  const handleChangeDetailedReason = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    noteRef.current = e.target.value;
  };

  const handleChangeNote = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChangeReturnRequestItems?.(id, {
      customerNote: e.target.value,
    });
  };

  const fileName = useMemo(() => {
    if (!images) return "";
    return images.map((imageFile) => imageFile.name).join(", ");
  }, [images]);

  const resetInputFile = () => {
    onChangeReturnRequestItems?.(id, { image: null });
  };

  return (
    <>
      {currentStep === "request" && (
        <li
          className={classNames(
            "pc:col-span-5 col-span-8 flex flex-col gap-2.5",
            disabled || (isGift && "pointer-events-none"),
          )}
        >
          <div className="pc:gap-5 relative flex items-start gap-2">
            <Input
              checked={returnRequestItem?.isCheck ?? false}
              disabled={disabled || isGift}
              id={id}
              onChange={(e) => {
                onChangeReturnRequestItems?.(id, { isCheck: e.target.checked });
              }}
              type="checkbox"
            />

            <div className="pc:gap-5 flex w-full gap-2">
              <Image
                alt={title}
                className={classNames("h-fit w-20 min-w-20", disabled && "opacity-50")}
                src={imageUrl}
              />
              <div className="pc:justify-start pc:gap-2.5 flex w-full flex-col justify-between">
                <div
                  className={classNames(
                    "pc:flex-row pc:gap-0 flex flex-col justify-between gap-1",
                    disabled && "text-grey-400",
                  )}
                >
                  <span>
                    {title || ""} {variantTitle || ""}
                  </span>
                  <Price
                    className={classNames(disabled && "text-grey-400")}
                    compareAtPrice={
                      (returnRequestItem?.quantity || 1) * (Number(compareAtPrice || "0") / originalQuantity)
                    }
                    price={(returnRequestItem?.quantity || 1) * (Number(price || "0") / originalQuantity)}
                  />
                </div>
                {!disabled &&
                  !isGift &&
                  (returnQuantity > 1 ? (
                    <Stepper
                      handleChange={handleStepper}
                      quantity={returnRequestItem?.quantity || 1}
                      quantityAvailable={returnQuantity}
                    />
                  ) : (
                    <span>Qty {returnRequestItem?.quantity || 1}</span>
                  ))}
                {disabled ? <span className="text-status-error">{t("return.disabled_product")}</span> : null}
              </div>
            </div>
          </div>

          {!disabled && !isGift && (
            <div className="pc:pl-[calc(20px+80px+20px)] flex flex-col gap-2.5">
              <Select as="square" disabled={disabled} onChange={handleChangeReason} options={RETURN_REASON_OPTIONS} />
              {returnRequestItem?.returnReason === ReturnReason.UNWANTED && <p>{t("return.unwanted_notice")}</p>}
              {(returnRequestItem?.returnReason === ReturnReason.DEFECTIVE ||
                returnRequestItem?.returnReason === ReturnReason.WRONG_ITEM) && (
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-1">
                    <Input
                      className="lg:max-w-screen-grid-cols-3 w-full"
                      files={fileName}
                      onChange={handleInputFile}
                      onClickInputFileButton={resetInputFile}
                      placeholder={t("return.image_description")}
                      ref={inputRef}
                      type="file"
                    />
                  </div>
                  <div className="flex flex-col gap-2.5">
                    <TextArea
                      className="lg:max-w-screen-grid-cols-3 w-full"
                      maxLength={300}
                      onBlur={handleChangeNote}
                      onChange={handleChangeDetailedReason}
                      placeholder={t("return.detail_description")}
                      rows={1}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </li>
      )}

      {currentStep === "requested" && (
        <li
          className={classNames(
            "pc:col-span-5 pc:grid-cols-subgrid col-span-8 grid grid-cols-subgrid",
            disabled && "pointer-events-none",
          )}
        >
          <div className="pc:col-span-3 pc:gap-5 col-span-6 flex gap-2">
            <Image alt={title} className="h-fit w-20 min-w-20" src={imageUrl} />
            <div className="flex grow flex-col gap-1">
              <div className="pc:flex-row pc:gap-1 flex flex-col">
                <span>
                  {title || ""} {(isPC && variantTitle) || ""}
                </span>
              </div>
              {!isPC && <span>{variantTitle || ""}</span>}
              {!isPC && <span>Qty {returnQuantity}</span>}
              <div className="flex flex-col gap-1">
                <span>{translationReturnReason[returnReason as keyof typeof translationReturnReason]}</span>
                {customerNote ? <p>{customerNote}</p> : null}
              </div>
            </div>
          </div>
          {isPC ? (
            <span className="pc:col-span-1">{returnQuantity ? <span>Qty {returnQuantity}</span> : null}</span>
          ) : null}
          <Price
            className="pc:col-span-1 col-span-2 justify-end text-right"
            compareAtPrice={(Number(compareAtPrice || "0") / originalQuantity) * returnQuantity}
            price={(Number(price || "0") / originalQuantity) * returnQuantity}
          />
        </li>
      )}
    </>
  );
}
